import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Play, Link } from "lucide-react";
import { ContentBlock } from "@/types/case-study";
import { useImageUpload } from "@/hooks/domains/storage/useImageUpload";
import { useAuth } from "@/hooks/domains/auth/useAuth";

interface VideoBlockProps {
  block: ContentBlock;
  onUpdate: (content: any) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
}

export const VideoBlock: React.FC<VideoBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [videoUrl, setVideoUrl] = React.useState(block.content?.url || "");
  const [caption, setCaption] = React.useState(block.content?.caption || "");
  
  const {
    uploading,
    handleImageUpload,
  } = useImageUpload({
    user,
    onImageUrlChange: (urls) => {
      if (urls.length > 0) {
        onUpdate({
          url: urls[0],
          caption: caption,
          type: "uploaded",
        });
      }
    },
    bucketName: "case-study-images",
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e);
  };

  const handleUrlChange = (url: string) => {
    setVideoUrl(url);
    onUpdate({
      url,
      caption,
      type: "url",
    });
  };

  const handleCaptionChange = (newCaption: string) => {
    setCaption(newCaption);
    onUpdate({
      url: videoUrl,
      caption: newCaption,
      type: block.content?.type || "url",
    });
  };

  const isUrl = block.content?.type === "url" || (!block.content?.type && videoUrl.startsWith("http"));
  const currentUrl = block.content?.url || videoUrl;

  return (
    <div className={`group relative ${isActive ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        {currentUrl ? (
          <div className="space-y-3">
            <div className="relative">
              {isUrl ? (
                <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Play className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Video URL: {currentUrl}</p>
                  </div>
                </div>
              ) : (
                <video
                  src={currentUrl}
                  controls
                  className="w-full h-auto max-h-96 rounded-lg"
                />
              )}
      {isActive && isRemovable && onRemove && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-70 hover:opacity-100 transition-opacity hover:bg-red-600"
          title="Delete this content block"
        >
          ×
        </button>
      )}
            </div>
            {isActive ? (
              <Input
                value={caption}
                onChange={(e) => handleCaptionChange(e.target.value)}
                placeholder="Add a caption..."
                className="text-sm"
              />
            ) : (
              <div className="text-sm text-gray-600 italic">
                {caption || "No caption"}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Play className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Add a video</p>
            <div className="space-y-2">
              <Input
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="Paste video URL (YouTube, Vimeo, etc.)"
                className="text-sm"
              />
              <Button
                onClick={() => handleUrlChange(videoUrl)}
                disabled={!videoUrl.trim()}
                size="sm"
                className="w-full"
              >
                <Link className="w-4 h-4 mr-2" />
                Add Video URL
              </Button>
              <div className="text-xs text-gray-500">or</div>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : "Upload Video File"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

